import { format, getDate, getDay, getDayOfYear, getHours, getISOWeek, getMinutes, getMonth, getQuarter, getSeconds, getYear, isValid, startOfDay, startOfHour, startOfMinute, startOfMonth, startOfQuarter, startOfSecond, startOfWeek, startOfYear, } from 'date-fns';
import { toZonedTime, fromZonedTime } from 'date-fns-tz';
import { DataType } from '../../DataType';
import { FFuncScalar } from '../FormulaFunc';
import { NodeType } from '../Parser/Node';
export var DatePart;
(function (DatePart) {
    DatePart["YEAR"] = "year";
    DatePart["QUARTER"] = "quarter";
    DatePart["MONTH"] = "month";
    DatePart["DAYOFYEAR"] = "dayofyear";
    DatePart["DAY"] = "day";
    DatePart["WEEKDAY"] = "weekday";
    DatePart["WEEK"] = "week";
    DatePart["HOUR"] = "hour";
    DatePart["MINUTE"] = "minute";
    DatePart["SECOND"] = "second";
})(DatePart || (DatePart = {}));
export class DATENAME extends FFuncScalar {
    constructor(args) {
        super('DATENAME', args);
    }
    getReturnType(argTypes) {
        return DataType.STRING;
    }
    value(scope, ...args) {
        const mVal = args[1](scope);
        if (!isValid(mVal)) {
            return mVal == null
                ? null
                : // Same return value as formatting an invalid moment
                    'Invalid date';
        }
        const mDate = toZonedTime(mVal, 'Z');
        const datePart = DATEPART.GetDatePart(args[0](scope));
        switch (datePart) {
            case DatePart.YEAR:
                return format(mDate, 'yyyy');
            case DatePart.QUARTER:
                return format(mDate, 'Q');
            case DatePart.MONTH:
                return format(mDate, 'MMMM');
            case DatePart.DAYOFYEAR:
                return format(mDate, 'D', { useAdditionalDayOfYearTokens: true });
            case DatePart.DAY:
                return format(mDate, 'd');
            case DatePart.WEEKDAY:
                return format(mDate, 'eeee');
            case DatePart.WEEK:
                return format(mDate, 'w');
            case DatePart.HOUR:
                return format(mDate, 'k');
            case DatePart.MINUTE:
                return format(mDate, 'm');
            case DatePart.SECOND:
                return format(mDate, 's');
        }
    }
}
export class DATEPART extends FFuncScalar {
    static GetDatePart(name) {
        switch (name === null || name === void 0 ? void 0 : name.toLowerCase()) {
            case 'year':
                return DatePart.YEAR;
            case 'quarter':
                return DatePart.QUARTER;
            case 'month':
                return DatePart.MONTH;
            case 'dayofyear':
                return DatePart.DAYOFYEAR;
            case 'day':
                return DatePart.DAY;
            case 'weekday':
                return DatePart.WEEKDAY;
            case 'week':
                return DatePart.WEEK;
            case 'hour':
                return DatePart.HOUR;
            case 'minute':
                return DatePart.MINUTE;
            case 'second':
                return DatePart.SECOND;
        }
    }
    constructor(args) {
        super('DATEPART', args);
    }
    getReturnType(argTypes) {
        if (this.argc > 0) {
            const arg = this.args[0];
            if (arg.type === NodeType.StringLiteral) {
                const partNode = arg;
                const datePart = DATEPART.GetDatePart(partNode.value);
                switch (datePart) {
                    case DatePart.WEEKDAY:
                        return DataType.DATEWKDAY;
                    case DatePart.MONTH:
                        return DataType.DATEMONTH;
                    case DatePart.QUARTER:
                        return DataType.DATEQTR;
                }
            }
        }
        return DataType.INTEGER;
    }
    value(scope, ...args) {
        const mVal = args[1](scope);
        if (!isValid(mVal)) {
            return mVal == null
                ? null
                : // Same return value as the date-part getters of an invalid moment
                    NaN;
        }
        const mDate = toZonedTime(mVal, 'Z');
        const datePart = DATEPART.GetDatePart(args[0](scope));
        switch (datePart) {
            case DatePart.YEAR:
                return getYear(mDate);
            case DatePart.QUARTER:
                return getQuarter(mDate);
            case DatePart.MONTH:
                return getMonth(mDate) + 1;
            case DatePart.DAYOFYEAR:
                return getDayOfYear(mDate);
            case DatePart.WEEK:
                return getISOWeek(mDate);
            // Day of month
            case DatePart.DAY:
                return getDate(mDate);
            // Day of week
            case DatePart.WEEKDAY:
                return getDay(mDate);
            case DatePart.HOUR:
                return getHours(mDate);
            case DatePart.MINUTE:
                return getMinutes(mDate);
            case DatePart.SECOND:
                return getSeconds(mDate);
        }
    }
}
export class DATETRUNC extends FFuncScalar {
    constructor(args) {
        super('DATETRUNC', args);
    }
    getReturnType(argTypes) {
        return DataType.DATETIME;
    }
    value(scope, ...args) {
        const mVal = args[1](scope);
        if (!isValid(mVal)) {
            return mVal == null
                ? null
                : // Same return value as the date-trunc methods of an invalid moment
                    NaN;
        }
        const fDate = toZonedTime(mVal, 'Z');
        const datePart = DATEPART.GetDatePart(args[0](scope));
        let value;
        switch (datePart) {
            case DatePart.YEAR:
                value = startOfYear(fDate);
                break;
            case DatePart.QUARTER:
                value = startOfQuarter(fDate);
                break;
            case DatePart.MONTH:
                value = startOfMonth(fDate);
                break;
            case DatePart.DAYOFYEAR:
                value = startOfDay(fDate);
                break;
            case DatePart.DAY:
                value = startOfDay(fDate);
                break;
            case DatePart.WEEKDAY:
                value = startOfDay(fDate);
                break;
            case DatePart.WEEK:
                value = startOfWeek(fDate);
                break;
            case DatePart.HOUR:
                value = startOfHour(fDate);
                break;
            case DatePart.MINUTE:
                value = startOfMinute(fDate);
                break;
            case DatePart.SECOND:
                value = startOfSecond(fDate);
                break;
        }
        if (isValid(value)) {
            return fromZonedTime(value, 'Z').getTime();
        }
        return NaN;
    }
}
